import { h, on, empty } from '../../../../../src/shared/components/dom.js';
import { Spinner } from '../../../../../src/shared/components/spinner.js';
import { EmptyState } from '../../../../../src/shared/components/empty-state.js';
import { LibraryGridTile as GridTile } from '../../components/library-grid-tile.js';
import { state } from '../../state.js';
import { navigate } from '../../router.js';
import { libraryPlayUrl } from '../../play-url.js';
import { releaseTypesParam, includedHiddenTypes, sanitizeReleaseTypes } from '../../browse-state.js';
import {
  getFilter,
  getFilterSchema,
  createFilter,
  updateFilter,
  deleteFilter,
  previewCatalog,
  getGenres,
  getTags,
} from '../../api.js';

/**
 * Filter builder screen — /library/filters/new and /library/filters/:id
 *
 * Lets the user compose a saved filter rule visually. The rule is a
 * tree of conditions and nested AND/OR groups. As the user edits, a
 * live preview shows the first 12 matching tiles so they can see the
 * rule's effect without saving.
 *
 * The field/op allowlist comes from the server at boot via
 * getFilterSchema — the UI dropdowns NEVER hardcode the list, so it
 * can't drift from the compiler's accept set.
 *
 * Rule shape (mirrors the server rule-compiler):
 *   { combinator: 'and' | 'or', conditions: [ <rule> | { field, op, value } ] }
 */

const DEFAULT_RULE = {
  combinator: 'and',
  conditions: [{ field: 'genre', op: 'eq', value: '' }],
};

export async function render(params, { root, isCurrent = () => true }) {
  empty(root);
  root.appendChild(Spinner({ label: 'Loading filter…' }));

  const isNew = !params.id;
  let filter = { name: '', rule: structuredClone(DEFAULT_RULE) };
  let schema;
  // Suggestion lists for autocomplete datalists. Best-effort: if either
  // endpoint 404s on an older server or returns empty, the inputs just
  // render without typeahead — no hard failure.
  let genres = [];
  let tags = [];

  try {
    // Parallelize schema + suggestion loads — both are cheap, and the
    // suggestion fetches must not block the builder on slow network.
    const [schemaRes, genresRes, tagsRes] = await Promise.all([
      getFilterSchema(),
      getGenres().catch(() => []),
      getTags().catch(() => []),
    ]);
    schema = schemaRes;
    genres = Array.isArray(genresRes) ? genresRes : [];
    tags = Array.isArray(tagsRes) ? tagsRes : [];
    if (!isNew) {
      const existing = await getFilter(params.id);
      filter = { id: existing.id, name: existing.name, rule: existing.rule };
    }
    if (!isCurrent()) return false;
  } catch (err) {
    if (!isCurrent()) return false;
    empty(root);
    root.appendChild(EmptyState({
      title: err.status === 401 ? 'Sign in required' : 'Couldn\'t load filter',
      message: err.status === 401
        ? 'Saved filters are per-user. Sign in from the main app first.'
        : (err.status === 404 ? 'That filter doesn\'t exist or isn\'t yours.' : 'Try again in a moment.'),
      action: h('a', { class: 'lib-btn', href: '/library' }, '← Back to library'),
      headingLevel: 'h1',
    }));
    return false;
  }

  empty(root);
  root.appendChild(buildScreen({ filter, schema, isNew, genres, tags }));
  return true;
}

function buildScreen({ filter, schema, isNew, genres = [], tags = [] }) {
  const screen = h('section', { class: 'lib-filter-builder' });

  // ── Suggestion lists for inline autocomplete ─────────────────────────
  //
  // Flatten the endpoint payloads into plain strings once so the
  // completion handler doesn't re-map on every keystroke. The server
  // orders by usage count DESC so the most-played genres/tags surface
  // first — which is what the user most likely wants when they type
  // a common prefix like "A".
  const genreStrings = genres.map(g => g.genre).filter(Boolean);
  const tagStrings = tags.map(t => t.slug).filter(Boolean);

  // ── Two columns: composer on left (with all chrome), preview on right ──
  //
  // All the page chrome (back link, title, name + save row, status)
  // lives inside the composer column now so the preview can occupy the
  // full height of the viewport below the topbar. The composer scrolls
  // independently within its own column if the rule tree gets tall.
  const composerWrap = h('div', { class: 'lib-filter-composer' });
  const previewWrap = h('div', { class: 'lib-filter-preview' });
  const previewHeading = h('h2', { class: 'lib-game-section-title', tabindex: '-1' }, 'Preview');
  const previewHint = h('p', { role: 'status', 'aria-live': 'polite' });
  const previewContent = h('div');
  previewWrap.append(previewHeading, previewHint, previewContent);

  // Rule section is a sub-container so renderComposer() only empties
  // the rule tree — not the surrounding chrome (title, name row, etc).
  const ruleSection = h('div', { class: 'lib-filter-rule-section' });

  // ── Name + primary action row (replaces the old page-level header) ──
  const nameInput = h('input', {
    type: 'text',
    class: 'lib-search lib-filter-name-input',
    placeholder: 'Name this filter…',
    value: filter.name || '',
    maxlength: '80',
    'aria-label': 'Filter name',
    oninput: () => updateSaveState(),
  });
  const saveBtn = h('button', {
    type: 'button',
    class: 'lib-btn lib-btn-primary lib-filter-save-btn',
    onclick: () => onSave(),
  }, isNew ? 'Create filter' : 'Save changes');
  const deleteBtn = !isNew ? h('button', {
    type: 'button',
    class: 'lib-btn lib-filter-delete-btn',
    onclick: () => onDelete(),
  }, 'Delete') : null;

  const statusEl = h('p', { class: 'lib-filter-status', 'aria-live': 'polite' });

  const nameRow = h('div', { class: 'lib-filter-name-row' }, nameInput, saveBtn);
  if (deleteBtn) nameRow.appendChild(deleteBtn);

  composerWrap.appendChild(h('a', { class: 'lib-game-back', href: '/library' }, '← Library'));
  composerWrap.appendChild(
    h('h1', { class: 'lib-filter-title' }, isNew ? 'New filter' : 'Edit filter')
  );
  composerWrap.appendChild(nameRow);
  composerWrap.appendChild(statusEl);
  composerWrap.appendChild(ruleSection);

  screen.appendChild(
    h('div', { class: 'lib-filter-grid' }, composerWrap, previewWrap)
  );

  // Hoist preview state above first renderComposer() call so the
  // debounce timer and token are in-scope when schedulePreview fires.
  let previewTimer = null;
  let previewToken = 0;
  let previewAbort = null;
  // Signature of the last pruned rule we previewed. When a UI edit
  // doesn't change the pruned (value-bearing) tree — e.g. the user
  // just added an empty row or flipped the op on a still-empty leaf —
  // we skip the reload instead of burning a catalog fetch.
  let lastPreviewKey = null;

  renderComposer();
  schedulePreview();
  updateSaveState();
  let wasMounted = false;
  const unsubscribeSelection = state.subscribe(
    s => `${s.features?.library_release_types === true}:${JSON.stringify(s.catalog.filters?.release_types)}`,
    () => {
      if (screen.isConnected) {
        wasMounted = true;
        schedulePreview();
      } else if (wasMounted) {
        unsubscribeSelection();
        clearTimeout(previewTimer);
        if (previewAbort) previewAbort.abort();
      }
    },
  );

  /**
   * Toggle the save button's enabled/disabled state based on whether
   * the filter is currently savable:
   *   - name must be non-empty (trim)
   *   - rule must have at least one leaf with a real value
   * Dimmed + not-allowed cursor when disabled so the user sees clearly
   * that the button isn't actionable yet. Fixes the confusion of a
   * "Create filter" button that silently errored on an empty form.
   */
  function updateSaveState() {
    const hasName = nameInput.value.trim().length > 0;
    const hasRule = ruleHasContent(filter.rule);
    const enabled = hasName && hasRule;
    saveBtn.disabled = !enabled;
    saveBtn.classList.toggle('lib-btn-disabled', !enabled);
    saveBtn.title = enabled
      ? ''
      : !hasName
        ? 'Name your filter to enable saving'
        : 'Add at least one complete condition';
  }

  function renderComposer() {
    empty(ruleSection);
    ruleSection.appendChild(
      h('h2', { class: 'lib-game-section-title' }, 'Rule'),
    );
    ruleSection.appendChild(renderNode(filter.rule, filter.rule, null, 0));
    schedulePreview();
    updateSaveState();
  }

  /**
   * Render a node (group or leaf). `parent` is the containing group;
   * `indexInParent` lets Remove splice the array. `depth` caps nesting.
   */
  function renderNode(node, root, parent, depth) {
    if ('combinator' in node) return renderGroup(node, root, parent, depth);
    return renderLeaf(node, root, parent, depth);
  }

  function renderGroup(group, root, parent, depth) {
    const wrap = h('div', { class: `lib-rule-group lib-rule-depth-${depth}` });

    // Subgroups are always removable. The root group is never removable
    // (there's no parent to remove it from, and the tree must keep
    // at least one top-level condition).
    const isRoot = group === root;

    const header = h('div', { class: 'lib-rule-group-header' },
      h('select', {
        class: 'lib-select',
        onchange: (e) => { group.combinator = e.target.value; schedulePreview(); },
      },
        ...['and', 'or'].map(op =>
          h('option', { value: op, selected: group.combinator === op ? true : undefined },
            op.toUpperCase())
        ),
      ),
      h('span', { class: 'lib-rule-group-label' },
        isRoot ? 'Match' : 'Subgroup',
      ),
      isRoot ? null : h('button', {
        type: 'button',
        class: 'lib-rule-remove',
        title: 'Remove group',
        onclick: () => {
          const idx = parent.conditions.indexOf(group);
          if (idx >= 0) parent.conditions.splice(idx, 1);
          renderComposer();
        },
      }, '×'),
    );
    wrap.appendChild(header);

    const body = h('div', { class: 'lib-rule-group-body' });
    for (const child of group.conditions) {
      body.appendChild(renderNode(child, root, group, depth + 1));
    }
    wrap.appendChild(body);

    // Footer: add condition / add group
    wrap.appendChild(
      h('div', { class: 'lib-rule-group-footer' },
        h('button', {
          type: 'button',
          class: 'lib-btn lib-btn-ghost',
          onclick: () => {
            group.conditions.push({ field: 'genre', op: 'eq', value: '' });
            renderComposer();
          },
        }, '+ Condition'),
        depth < 4 ? h('button', {
          type: 'button',
          class: 'lib-btn lib-btn-ghost',
          onclick: () => {
            group.conditions.push({
              combinator: group.combinator === 'and' ? 'or' : 'and',
              conditions: [{ field: 'genre', op: 'eq', value: '' }],
            });
            renderComposer();
          },
        }, '+ Group') : null,
      )
    );

    return wrap;
  }

  function renderLeaf(leaf, _root, parent, _depth) {
    const wrap = h('div', { class: 'lib-rule-leaf' });

    // Field dropdown. Changing the field preserves the current value
    // when the new field's type matches the old (e.g. genre → developer
    // both take text). It only resets when the type changes (e.g.
    // genre → release_year) or when the current op isn't valid for the
    // new field.
    const oldFieldMeta = schema.fields[leaf.field] || { ops: ['eq'], type: 'text' };
    const fieldSel = h('select', {
      class: 'lib-select',
      onchange: (e) => {
        const newField = e.target.value;
        const newFieldMeta = schema.fields[newField];
        const sameType = oldFieldMeta.type === newFieldMeta.type;
        // Keep the current op if the new field also supports it; otherwise
        // fall back to the new field's first op.
        const newOp = newFieldMeta.ops.includes(leaf.op) ? leaf.op : newFieldMeta.ops[0];
        const newValue = sameType
          ? coerceValueForOp(leaf.value, leaf.op, newOp, newFieldMeta)
          : defaultValueForOp(newOp, newFieldMeta);
        leaf.field = newField;
        leaf.op = newOp;
        leaf.value = newValue;
        // Drop the `unit` on the leaf when leaving a time field — it
        // only makes sense for time-typed leaves, and the server
        // validator rejects it on other fields. buildUnitSelect will
        // re-seed it with the default when entering a time field.
        if (newFieldMeta.type !== 'time') delete leaf.unit;
        renderComposer();
      },
    }, ...Object.keys(schema.fields).map(name =>
      h('option', { value: name, selected: leaf.field === name ? true : undefined },
        prettyField(name))
    ));

    // Op dropdown (scoped to the current field's allowed ops). Preserves
    // the current value when the shape can be coerced to the new op
    // (scalar ↔ [scalar], scalar ↔ [scalar, scalar] for between, etc).
    const fieldMeta = schema.fields[leaf.field] || { ops: ['eq'], type: 'text' };
    const opSel = h('select', {
      class: 'lib-select',
      onchange: (e) => {
        const newOp = e.target.value;
        leaf.value = coerceValueForOp(leaf.value, leaf.op, newOp, fieldMeta);
        leaf.op = newOp;
        renderComposer();
      },
    }, ...fieldMeta.ops.map(op =>
      h('option', { value: op, selected: leaf.op === op ? true : undefined },
        prettyOp(op))
    ));

    // Value input (shape depends on op + type)
    const valueInput = buildValueInput(leaf, fieldMeta);

    // A condition is only deletable if its parent group still has ≥1
    // condition left afterwards. The root group always shows at least
    // one row — the first row can't be removed until a second is added.
    const removable = parent.conditions.length > 1;
    const removeBtn = h('button', {
      type: 'button',
      class: `lib-rule-remove${removable ? '' : ' disabled'}`,
      title: removable
        ? 'Remove condition'
        : 'A group must have at least one condition. Add another before removing this one',
      disabled: removable ? undefined : true,
      onclick: removable ? () => {
        const idx = parent.conditions.indexOf(leaf);
        if (idx >= 0) parent.conditions.splice(idx, 1);
        renderComposer();
      } : undefined,
    }, '×');

    wrap.append(fieldSel, opSel, valueInput, removeBtn);
    return wrap;
  }

  function buildValueInput(leaf, fieldMeta) {
    const wrap = h('div', { class: 'lib-rule-value-wrap' });
    const isNumeric = fieldMeta.type === 'number' || fieldMeta.type === 'time';

    // Suggestion list for inline autocomplete. Only scoped to fields
    // with a known-value catalog; other text fields (title, developer,
    // publisher) are free-form so autocomplete would be misleading.
    //
    // We also gate on the op: eq/neq want an exact known value (so
    // completing makes sense), while contains/starts_with are
    // intentionally partial (completing to a full value would defeat
    // the user's choice of those ops).
    const suggestions = leaf.field === 'genre' ? genreStrings
                      : leaf.field === 'tag'   ? tagStrings
                      : null;
    const autocompleteApplies = suggestions && suggestions.length
      && (leaf.op === 'eq' || leaf.op === 'neq');

    if (leaf.op === 'between') {
      // Two numeric inputs. Render blank when the value is null/undefined
      // so a fresh "between" leaf doesn't auto-fill 0. Clearing an input
      // stores null (not 0, which Number('') unhelpfully produces).
      if (!Array.isArray(leaf.value) || leaf.value.length !== 2) leaf.value = [null, null];
      const parseOrNull = (s) => s === '' ? null : Number(s);
      const lo = h('input', {
        type: 'number',
        class: 'lib-search',
        value: leaf.value[0] == null ? '' : String(leaf.value[0]),
        placeholder: 'min',
        'aria-label': `Minimum ${leaf.field}`,
        onchange: (e) => { leaf.value[0] = parseOrNull(e.target.value); schedulePreview(); },
      });
      const hi = h('input', {
        type: 'number',
        class: 'lib-search',
        value: leaf.value[1] == null ? '' : String(leaf.value[1]),
        placeholder: 'max',
        'aria-label': `Maximum ${leaf.field}`,
        onchange: (e) => { leaf.value[1] = parseOrNull(e.target.value); schedulePreview(); },
      });
      wrap.append(lo, h('span', null, '–'), hi);
      // Time fields get a unit picker alongside the between range — both
      // min and max share the same unit (the server multiplies each
      // through UNIT_FACTORS[unit] at query time).
      if (fieldMeta.type === 'time') wrap.appendChild(buildUnitSelect(leaf, fieldMeta));
      return wrap;
    }

    if (leaf.op === 'in' || leaf.op === 'not_in') {
      // Comma-separated list → array
      if (!Array.isArray(leaf.value)) leaf.value = [];
      const input = h('input', {
        type: 'text',
        class: 'lib-search',
        value: leaf.value.join(', '),
        placeholder: 'comma-separated values',
        'aria-label': `${leaf.field} values, comma separated`,
        onchange: (e) => {
          leaf.value = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
          if (isNumeric) leaf.value = leaf.value.map(Number);
          schedulePreview();
        },
      });
      wrap.append(input);
      return wrap;
    }

    if (fieldMeta.type === 'bool') {
      const sel = h('select', {
        class: 'lib-select',
        onchange: (e) => { leaf.value = e.target.value === 'true'; schedulePreview(); },
      },
        h('option', { value: 'true', selected: leaf.value === true ? true : undefined }, 'true'),
        h('option', { value: 'false', selected: leaf.value === false ? true : undefined }, 'false'),
      );
      wrap.append(sel);
      return wrap;
    }

    // Default: single text/number input. Numeric inputs store `null`
    // when blank (rather than Number('') === 0) so a fresh leaf renders
    // empty and `leafHasValue` correctly treats it as "no content".
    const input = h('input', {
      type: isNumeric ? 'number' : 'text',
      class: 'lib-search',
      value: leaf.value == null ? '' : String(leaf.value),
      placeholder: 'value',
      'aria-label': `${leaf.field} value`,
      onchange: (e) => {
        const raw = e.target.value;
        if (isNumeric) {
          leaf.value = raw === '' ? null : Number(raw);
        } else {
          leaf.value = raw;
        }
        schedulePreview();
      },
    });
    // Inline type-ahead completion for genre/tag eq/neq leaves — see
    // attachAutocomplete below. Skipped for free-form text fields and
    // for partial-match ops.
    if (autocompleteApplies) {
      attachAutocomplete(input, suggestions, leaf, schedulePreview);
    }
    wrap.append(input);
    // Time fields: append a unit selector (minutes / hours / seconds).
    // The `unit` lives on the leaf itself so the persisted rule JSON
    // preserves whatever the user typed — "2 hours" round-trips as
    // "2 hours" instead of "120 minutes".
    if (fieldMeta.type === 'time') wrap.appendChild(buildUnitSelect(leaf, fieldMeta));
    return wrap;
  }

  function buildUnitSelect(leaf, fieldMeta) {
    // Server-provided unit list — empty array means older server; fall
    // back to a sensible default. Keeps the dropdown in lockstep with
    // the compiler's ALLOWED_UNITS.
    const units = fieldMeta.units && fieldMeta.units.length ? fieldMeta.units : ['minutes', 'hours'];
    // Default to "hours" when unit is absent — most common threshold
    // ("played more than 2 hours") and matches defaultValueForOp.
    if (!leaf.unit) leaf.unit = units.includes('hours') ? 'hours' : units[0];
    return h('select', {
      class: 'lib-select lib-rule-unit-select',
      onchange: (e) => { leaf.unit = e.target.value; schedulePreview(); },
    },
      ...units.map(u =>
        h('option', { value: u, selected: leaf.unit === u ? true : undefined }, UNIT_LABELS[u] || u)
      ),
    );
  }

  // ── Live preview (debounced) ───────────────────────────────────────
  // (previewTimer + previewToken hoisted above renderComposer() call)

  function schedulePreview() {
    // Short-circuit when the pruned rule hasn't changed. Typical trigger:
    // the user added an empty condition row and is poking at the field
    // or op dropdowns — those edits change `filter.rule` but don't add
    // anything a predicate cares about, so re-running the preview is
    // wasted work.
    const pruned = pruneEmptyLeaves(filter.rule);
    const key = JSON.stringify([pruned || null, releaseTypesParam(state.get().catalog.filters, state.get().features)]);
    if (key === lastPreviewKey) return;
    lastPreviewKey = key;

    clearTimeout(previewTimer);
    ++previewToken;
    if (previewAbort) previewAbort.abort();
    previewTimer = setTimeout(runPreview, 300);
  }

  async function runPreview() {
    const token = ++previewToken;
    if (previewAbort) previewAbort.abort();
    previewAbort = null;
    previewHeading.replaceChildren('Preview');
    previewHint.replaceChildren();
    previewHint.classList.remove('lib-visually-hidden');
    empty(previewContent);

    const selection = releaseTypesParam(state.get().catalog.filters, state.get().features);
    if (selection === null) {
      previewHint.classList.add('lib-visually-hidden');
      previewHint.textContent = 'Choose a release type to see games.';
      previewContent.append(
        h('p', {}, 'Choose a release type to see games.'),
        h('button', { type: 'button', class: 'lib-btn', onclick: () => {
          state.update('catalog.filters', {
            ...state.get().catalog.filters, release_types: ['official'],
          });
          schedulePreview();
          previewHeading.focus();
        } }, 'Show official releases'),
      );
      return;
    }

    // Prune empty leaves before building the predicate. A half-typed
    // condition (e.g. genre eq "") would otherwise match nothing and
    // when AND'd with a populated sibling would zero the result set —
    // confusing the user who just added a new row to edit next. If
    // pruning leaves nothing behind, show the "add a condition" hint.
    const prunedRule = pruneEmptyLeaves(filter.rule);
    if (!prunedRule) {
      previewContent.appendChild(
        h('p', { class: 'lib-tile-meta-faded' },
          'Add a condition to see matching games.')
      );
      return;
    }

    previewContent.appendChild(Spinner({ label: 'Matching…' }));

    // Evaluate the rule on the server and return only the first 12 matches.
    // The total remains authoritative without downloading the full catalog.
    try {
      const controller = new AbortController();
      previewAbort = controller;
      const data = await previewCatalog(prunedRule, 12, {
        query: selection === undefined ? undefined : { release_types: selection },
        signal: controller.signal,
      });
      if (token !== previewToken) return;
      const matched = data.items || [];
      const total = Number(data.total) || 0;
      empty(previewContent);
      previewHeading.replaceChildren(
        'Preview',
        h('span', { class: 'lib-game-section-badge' }, `${total} match${total === 1 ? '' : 'es'}`)
      );

      const hidden = data.hidden_by_release_type;
      const types = state.get().features?.library_release_types === true
        ? includedHiddenTypes(state.get().catalog.filters?.release_types, hidden)
        : [];
      if (types.length) {
        previewHint.classList.remove('lib-visually-hidden');
        const count = Number(hidden.total);
        previewHint.append(
          document.createTextNode(`${count} more match${count === 1 ? '' : 'es'} ${count === 1 ? 'is' : 'are'} hidden by release type. `),
          h('button', { type: 'button', class: 'lib-btn', onclick: () => {
            state.update('catalog.filters', {
              ...state.get().catalog.filters,
              release_types: [...sanitizeReleaseTypes(state.get().catalog.filters.release_types), ...types],
            });
            schedulePreview();
            previewHeading.focus();
          } }, 'Include them'),
        );
      }

      if (matched.length === 0) {
        if (!types.length) previewContent.appendChild(h('p', { class: 'lib-tile-meta-faded' }, 'No games match this rule yet.'));
      } else {
        // Use the same GridTile component the library index uses — the
        // preview is showing the same kind of data, so hiding metadata
        // the user's filtering on (year, genre, rating, favorite) would
        // be silly. Click → detail page; the hover ▶ fires solo play.
        const grid = h('div', { class: 'lib-grid lib-grid-tiles lib-filter-preview-grid' });
        for (const item of matched) {
          grid.appendChild(GridTile({
            rom: item,
            onPlay: (rom) => {
              location.assign(libraryPlayUrl(rom.system, rom.name));
            },
            onDetail: (rom) => {
              const slug = rom.game_slug || rom.game_id || rom.hash;
              if (slug) navigate(`/library/g/${rom.game_system || rom.system}/${slug}`);
            },
          }));
        }
        previewContent.appendChild(grid);
      }
    } catch (err) {
      if (token !== previewToken) return;
      if (err?.name === 'AbortError') return;
      previewHint.replaceChildren();
      empty(previewContent);
      previewHeading.replaceChildren('Preview');
      previewContent.appendChild(h('p', { class: 'lib-tile-meta-faded' }, err.message || 'Preview failed'));
    } finally {
      if (token === previewToken) previewAbort = null;
    }
  }

  // ── Save / Delete ──────────────────────────────────────────────────
  async function onSave() {
    const name = nameInput.value.trim();
    if (!name) {
      setStatus('Name required', 'error');
      nameInput.focus();
      return;
    }
    // Prune empty leaves before saving — same reasoning as the preview:
    // half-typed conditions shouldn't be persisted, they'd make the
    // saved filter match nothing when applied from the sidebar.
    const pruned = pruneEmptyLeaves(filter.rule);
    if (!pruned) {
      setStatus('Add at least one complete condition before saving', 'error');
      return;
    }
    setStatus('Saving…', 'info');
    try {
      if (isNew) {
        const saved = await createFilter(name, pruned);
        setStatus('Saved', 'ok');
        navigate(`/library/filters/${saved.id}`, { replace: true });
      } else {
        await updateFilter(filter.id, { name, rule: pruned });
        setStatus('Saved', 'ok');
      }
    } catch (err) {
      setStatus(err?.body?.message || err?.body?.error || 'Save failed', 'error');
    }
  }

  async function onDelete() {
    if (!confirm('Delete this filter? This can\'t be undone.')) return;
    try {
      await deleteFilter(filter.id);
      navigate('/library');
    } catch (err) {
      setStatus(err?.body?.message || 'Delete failed', 'error');
    }
  }

  function setStatus(msg, kind = 'info') {
    statusEl.textContent = msg;
    statusEl.className = `lib-filter-status lib-filter-status-${kind}`;
  }

  return screen;
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Inline type-ahead completion for an input bound to a finite
 * suggestion list. Two match modes, tried in order:
 *
 *   PREFIX — suggestion starts with the typed text.
 *     e.g. "Ac" → "Action". Input becomes "Ac" + "tion" with "tion"
 *     selected. The next keystroke replaces the tail, smoothly
 *     narrowing character by character.
 *
 *   SUBSTRING — suggestion contains the typed text mid-string.
 *     e.g. "rpg" → "Console-Style RPG". Appending a tail doesn't
 *     work because the typed keyword isn't at the start, so we
 *     replace the input with the full match and select the WHOLE
 *     thing. Continued typing replaces the selection (fresh search),
 *     Tab/Right/End accepts the match.
 *
 * Prefix matches are preferred over substring matches so common short
 * completions stay clean — substring is the fallback when typing the
 * start of a longer multi-word genre/tag doesn't match anything.
 *
 * Non-insertion edits (backspace, delete, cut) never re-trigger
 * completion — so shrinking the input works naturally. Escape drops
 * whatever was added by autocomplete and restores the user's typed
 * prefix (tracked separately from input.value).
 *
 * `leaf.value` is kept in lockstep with the full input so the live
 * preview reflects the suggestion the user is about to accept.
 */
function attachAutocomplete(input, suggestions, leaf, schedulePreview) {
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('spellcheck', 'false');

  // What the user has literally typed (without any autocompleted tail
  // or replacement). Escape restores this.
  let typedText = input.value || '';

  input.addEventListener('input', (e) => {
    // `inputType` on InputEvent tells us how the value changed. Only
    // insertions should trigger completion — deletes must not snap
    // the value back to the last match (that would make backspace
    // effectively useless while a suggestion is selected).
    const isInsert = !e.inputType || e.inputType.startsWith('insert');
    const current = input.value;

    if (!current) {
      typedText = '';
      leaf.value = '';
      schedulePreview();
      return;
    }

    if (!isInsert) {
      // Delete / cut: whatever the user sees IS what they mean.
      typedText = current;
      leaf.value = current;
      schedulePreview();
      return;
    }

    typedText = current;
    const lower = current.toLowerCase();
    const result = findMatch(suggestions, lower);

    if (!result) {
      leaf.value = current;
      schedulePreview();
      return;
    }

    const { match, kind } = result;

    // Exact match on the whole value: nothing to extend or select.
    // Use the suggestion's canonical casing.
    if (match.length === current.length) {
      input.value = match;
      leaf.value = match;
      schedulePreview();
      return;
    }

    if (kind === 'prefix') {
      // Keep the user's typed casing for the head, append the
      // suggestion's tail, select the tail.
      input.value = current + match.slice(current.length);
      input.setSelectionRange(current.length, match.length);
      leaf.value = input.value;
    } else {
      // Substring match: the typed keyword isn't at the start, so
      // ghost-text tailing doesn't work. Place the whole match,
      // select it entirely — one more keystroke restarts the search,
      // Tab/Right accepts.
      input.value = match;
      input.setSelectionRange(0, match.length);
      leaf.value = match;
    }
    schedulePreview();
  });

  input.addEventListener('keydown', (e) => {
    // Escape → revert whatever autocomplete added/replaced, leaving
    // only what the user actually typed. Only fires when there's a
    // visible suggestion (selected range exists).
    if (e.key === 'Escape' && input.selectionStart < input.selectionEnd) {
      e.preventDefault();
      input.value = typedText;
      input.setSelectionRange(typedText.length, typedText.length);
      leaf.value = typedText;
      schedulePreview();
    }
  });
}

/**
 * Find the first matching suggestion, preferring prefix matches over
 * substring matches. Returns `{ match, kind }` or `null`.
 *
 * `typedLower` must already be lowercased — hot path, called on every
 * keystroke, and the lowercasing is the same value the caller uses
 * for other comparisons.
 */
function findMatch(suggestions, typedLower) {
  let prefix = null;
  let substring = null;
  for (const s of suggestions) {
    const sl = s.toLowerCase();
    if (!prefix && sl.startsWith(typedLower)) {
      prefix = s;
      break; // Prefix wins; no need to look further for a worse match.
    }
    if (!substring && sl.includes(typedLower)) substring = s;
  }
  if (prefix) return { match: prefix, kind: 'prefix' };
  if (substring) return { match: substring, kind: 'substring' };
  return null;
}

const FIELD_LABELS = {
  system: 'System',
  title: 'Title',
  genre: 'Genre',
  developer: 'Developer',
  publisher: 'Publisher',
  release_year: 'Release year',
  rating: 'Rating',
  playtime: 'Play time',
  tag: 'Tag',
  favorite: 'Favorite',
  has_artwork: 'Has artwork',
  played: 'Played',
};

// Seconds per unit — mirrors UNIT_FACTORS in the server rule-compiler
// so the live preview applies the same multiplier the server will on
// the real query. Kept in lockstep; drift would show up as a preview
// that doesn't match the saved filter result.
const UNIT_FACTORS = { seconds: 1, minutes: 60, hours: 3600 };
const UNIT_LABELS = { seconds: 'seconds', minutes: 'minutes', hours: 'hours' };

const OP_LABELS = {
  eq: 'equals',
  neq: 'does not equal',
  contains: 'contains',
  starts_with: 'starts with',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  between: 'between',
  in: 'is one of',
  not_in: 'is not one of',
};

function prettyField(name) { return FIELD_LABELS[name] || name; }
function prettyOp(op) { return OP_LABELS[op] || op; }

/**
 * Does a single leaf have a meaningful value the predicate can act on?
 * An empty leaf ("genre eq '' ") would be an "impossible" predicate
 * (matches nothing), which when AND'd with real conditions zeroes the
 * result set and confuses users mid-edit. `pruneEmptyLeaves` below uses
 * this to drop empty leaves before preview + save.
 */
function leafHasValue(leaf) {
  const { op, value } = leaf;
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') {
    // 0 is a legit filter value (e.g. release_year > 0), so count any
    // finite number as content — the user explicitly typed it.
    return Number.isFinite(value);
  }
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) {
    if (op === 'between') {
      return value.length === 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]));
    }
    return value.some(v => v !== '' && v !== null && v !== undefined);
  }
  return false;
}

/**
 * Return a copy of `node` with empty-value leaves removed, and any
 * groups that become empty after pruning removed too. Returns `null`
 * if the entire tree prunes away.
 *
 * This is what lets the user mid-edit have an incomplete condition
 * (e.g. they added a new row and haven't typed yet) without nuking the
 * preview — we silently ignore the empty bits until they're filled in.
 */
function pruneEmptyLeaves(node) {
  if (!node) return null;
  if ('combinator' in node) {
    const kept = node.conditions
      .map(pruneEmptyLeaves)
      .filter(Boolean);
    if (kept.length === 0) return null;
    return { combinator: node.combinator, conditions: kept };
  }
  return leafHasValue(node) ? node : null;
}

/**
 * Is this rule "meaningful enough" to run a preview against? True when
 * at least one leaf has a non-empty value after pruning.
 */
function ruleHasContent(node) {
  return pruneEmptyLeaves(node) !== null;
}

/**
 * Reshape an existing value so it fits the target op without losing the
 * user's input. Called when the user changes the operator (or switches
 * to a same-type field). Examples:
 *
 *   scalar → `in` / `not_in`         → wrap in a single-element array
 *   scalar → `between`               → [scalar, scalar] (both sides seeded)
 *   array  → scalar op (eq, lt, etc) → take the first element
 *   between → `in`                    → preserve the two values as an array
 *
 * When the value can't be coerced meaningfully (empty string, array of
 * nothing), fall back to the default for the new op.
 */
function coerceValueForOp(oldValue, oldOp, newOp, fieldMeta) {
  // No-op when the op didn't change.
  if (oldOp === newOp) return oldValue;

  const isArrayOp = (op) => op === 'in' || op === 'not_in' || op === 'between';
  const isEmpty = (v) => v === '' || v === null || v === undefined;
  const isNumeric = fieldMeta.type === 'number' || fieldMeta.type === 'time';

  // If the old value is empty/unset, just pick a default for the new op.
  if (isEmpty(oldValue) || (Array.isArray(oldValue) && oldValue.length === 0)) {
    return defaultValueForOp(newOp, fieldMeta);
  }

  const oldIsArray = Array.isArray(oldValue);

  // Target: `between` needs exactly [min, max].
  if (newOp === 'between') {
    if (oldIsArray) {
      if (oldValue.length >= 2) return [Number(oldValue[0]), Number(oldValue[1])];
      if (oldValue.length === 1) return [Number(oldValue[0]), Number(oldValue[0])];
      return defaultValueForOp(newOp, fieldMeta);
    }
    const n = Number(oldValue);
    if (Number.isFinite(n)) return [n, n];
    return defaultValueForOp(newOp, fieldMeta);
  }

  // Target: `in` / `not_in` need an array.
  if (newOp === 'in' || newOp === 'not_in') {
    if (oldIsArray) return oldValue.filter(v => !isEmpty(v));
    return [oldValue];
  }

  // Target: scalar op. Take first element of an array if that's what we have.
  if (oldIsArray) {
    const first = oldValue.find(v => !isEmpty(v));
    if (first === undefined) return defaultValueForOp(newOp, fieldMeta);
    return isNumeric ? Number(first) : first;
  }

  // Scalar → scalar op — preserve as-is. Type coerce if going text ↔ number.
  if (isNumeric) {
    const n = Number(oldValue);
    return Number.isFinite(n) ? n : defaultValueForOp(newOp, fieldMeta);
  }
  if (fieldMeta.type === 'bool') {
    return typeof oldValue === 'boolean' ? oldValue : defaultValueForOp(newOp, fieldMeta);
  }
  return oldValue;
}

function defaultValueForOp(op, fieldMeta) {
  // Array ops always start empty.
  if (op === 'in' || op === 'not_in') return [];
  // `between` starts as [null, null] so both inputs render blank. The
  // validator treats [null, null] as "no content" (via leafHasValue), so
  // the preview stays quiet until the user types real numbers.
  if (op === 'between') return [null, null];
  if (fieldMeta.type === 'bool') return false;
  // Scalar numeric + time: start blank (null) rather than 0. Users
  // almost never want to filter on "= 0" for release_year/rating/
  // playtime, and a pre-filled 0 was confusing (auto-triggered the
  // preview). The unit dropdown handles its own default separately.
  if (fieldMeta.type === 'number' || fieldMeta.type === 'time') return null;
  return '';
}

/**
 * Client-side predicate builder — mirrors the semantics of
 * src/server/library/rule-compiler.js for the live preview. Kept
 * minimal; the server remains the enforcement point on actual catalog
 * queries. If these drift, the worst outcome is a preview that looks
 * slightly different from the saved result, which we catch in QA.
 */
function buildClientPredicate(node) {
  if ('combinator' in node) {
    const subs = node.conditions.map(buildClientPredicate);
    return node.combinator === 'and'
      ? (item) => subs.every(p => p(item))
      : (item) => subs.some(p => p(item));
  }
  const { field, op, value, unit } = node;

  const get = (item) => {
    switch (field) {
      case 'system':       return item.system;
      case 'title':        return item.displayName || item.title || '';
      case 'genre':        return item.genres || [];
      case 'developer':    return item.developer;
      case 'publisher':    return item.publisher;
      case 'release_year': return item.release_year;
      case 'rating':       return item.rating;
      case 'playtime':     return item.play_time_seconds || 0;
      case 'tag':          return item.tagSlugs || [];
      case 'favorite':     return !!item.favorite;
      case 'has_artwork':  return !!item.cover_url;
      case 'played':       return (item.play_time_seconds || 0) > 0;
    }
    return null;
  };

  const multi = (field === 'genre' || field === 'tag');
  const isTime = (field === 'playtime');
  // Multiply the comparison value by the unit factor so the predicate
  // does one scalar compare against the item's seconds column. Mirrors
  // the server compiler; drift here = preview disagreeing with the
  // saved result.
  const factor = isTime ? (UNIT_FACTORS[unit] || 1) : 1;
  const scale = (v) => Number(v) * factor;
  const textEq = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

  switch (op) {
    case 'eq':
      if (multi) return (it) => {
        const v = get(it);
        return Array.isArray(v) && v.some(x => textEq(x, value));
      };
      if (typeof value === 'boolean') return (it) => get(it) === value;
      if (typeof value === 'number') return (it) => Number(get(it)) === scale(value);
      return (it) => textEq(get(it), value);
    case 'neq':
      if (multi) return (it) => {
        const v = get(it);
        return !Array.isArray(v) || !v.some(x => textEq(x, value));
      };
      if (typeof value === 'boolean') return (it) => get(it) !== value;
      if (typeof value === 'number') return (it) => Number(get(it)) !== scale(value);
      return (it) => !textEq(get(it), value);
    case 'contains': {
      const needle = String(value || '').toLowerCase();
      // Multi-valued fields (genre, tag): match if ANY element contains
      // the substring. Scalar: the single value must contain it. Mirrors
      // the server rule-compiler so live preview doesn't diverge.
      if (multi) return (it) => {
        const v = get(it);
        return Array.isArray(v) && v.some(x => String(x || '').toLowerCase().includes(needle));
      };
      return (it) => String(get(it) || '').toLowerCase().includes(needle);
    }
    case 'starts_with': {
      const needle = String(value || '').toLowerCase();
      if (multi) return (it) => {
        const v = get(it);
        return Array.isArray(v) && v.some(x => String(x || '').toLowerCase().startsWith(needle));
      };
      return (it) => String(get(it) || '').toLowerCase().startsWith(needle);
    }
    case 'lt':  return (it) => Number(get(it)) <  scale(value);
    case 'lte': return (it) => Number(get(it)) <= scale(value);
    case 'gt':  return (it) => Number(get(it)) >  scale(value);
    case 'gte': return (it) => Number(get(it)) >= scale(value);
    case 'between': {
      const [lo, hi] = [scale(value[0]), scale(value[1])];
      return (it) => {
        const n = Number(get(it));
        return Number.isFinite(n) && n >= lo && n <= hi;
      };
    }
    case 'in': {
      const set = new Set((value || []).map(v => String(v).toLowerCase()));
      if (multi) return (it) => {
        const v = get(it);
        return Array.isArray(v) && v.some(x => set.has(String(x).toLowerCase()));
      };
      return (it) => set.has(String(get(it) || '').toLowerCase());
    }
    case 'not_in': {
      const set = new Set((value || []).map(v => String(v).toLowerCase()));
      if (multi) return (it) => {
        const v = get(it);
        return !Array.isArray(v) || !v.some(x => set.has(String(x).toLowerCase()));
      };
      return (it) => !set.has(String(get(it) || '').toLowerCase());
    }
  }
  return () => false;
}
